import React, { useEffect, useState } from "react";
import { Badge, Box, Typography, Tabs, Tab } from "@mui/material";
import ParentList from "./ParentList";
import PaperPurchases from "./PaperPurchases";
import MobileUsers from "./MobileUsers";
import AllTransactions from "./AllTransactions";
import RefundRequests from "./RefundRequests";
import api from "../../api";
import { adminHasPermission } from "../../utils/adminPermissions";
import useRefundRequestsEnabled from "../../hooks/useRefundRequestsEnabled";

const TAB_ALL = 0;
const TAB_COURSE = 1;
const TAB_MOCK = 2;
const TAB_PAPER = 3;
const TAB_MOBILE = 4;
const TAB_REFUND_REQUESTS = 5;

const AdminBillingTransactions = () => {
  const [tabValue, setTabValue] = useState(0);
  const canRefund = adminHasPermission("refund-transactions");
  // Customer refund requests: only when the backend's REFUND_CUSTOMER_REQUESTS_ENABLED is on.
  // Admins always refund from All Transactions and Mobile App.
  const refundRequestsEnabled = useRefundRequestsEnabled();
  const showRefundRequests = canRefund && refundRequestsEnabled;
  const [pendingRequests, setPendingRequests] = useState(0);

  // Badge count for the Refund Requests tab, so a waiting request is visible from any tab.
  useEffect(() => {
    if (!showRefundRequests) return;
    api
      .get("admin/billing/refund-requests", { params: { status: "open", per_page: 1 } })
      .then((res) => setPendingRequests(res.data?.data?.pending_count || 0))
      .catch(() => {});
  }, [showRefundRequests]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 1 }}>
          Billing &amp; Transactions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
          Every paid transaction in one list, plus the per-product views.
        </Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          mb: 2,
          "& .MuiTab-root": { fontWeight: 600, textTransform: "none" },
          "& .Mui-selected": { color: "primary.main" },
        }}
      >
        <Tab label="All Transactions" id="billing-tab-0" aria-controls="billing-panel-0" />
        <Tab label="Course" id="billing-tab-1" aria-controls="billing-panel-1" />
        <Tab label="Mock Exam" id="billing-tab-2" aria-controls="billing-panel-2" />
        <Tab label="Paper" id="billing-tab-3" aria-controls="billing-panel-3" />
        <Tab label="Mobile App" id="billing-tab-4" aria-controls="billing-panel-4" />
        {showRefundRequests && (
          <Tab
            value={TAB_REFUND_REQUESTS}
            id="billing-tab-5"
            aria-controls="billing-panel-5"
            label={
              <Badge color="error" badgeContent={pendingRequests} sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                Refund Requests
              </Badge>
            }
          />
        )}
      </Tabs>

      <Box role="tabpanel" hidden={tabValue !== TAB_ALL} id="billing-panel-0" aria-labelledby="billing-tab-0">
        {tabValue === TAB_ALL && (
          <Box sx={{ pt: 0 }}>
            <AllTransactions />
          </Box>
        )}
      </Box>
      <Box role="tabpanel" hidden={tabValue !== TAB_COURSE} id="billing-panel-1" aria-labelledby="billing-tab-1">
        {tabValue === TAB_COURSE && (
          <Box sx={{ pt: 0 }}>
            <ParentList forceBillingView billingType="course" />
          </Box>
        )}
      </Box>
      <Box role="tabpanel" hidden={tabValue !== TAB_MOCK} id="billing-panel-2" aria-labelledby="billing-tab-2">
        {tabValue === TAB_MOCK && (
          <Box sx={{ pt: 0 }}>
            <ParentList forceBillingView billingType="mock" />
          </Box>
        )}
      </Box>
      <Box role="tabpanel" hidden={tabValue !== TAB_PAPER} id="billing-panel-3" aria-labelledby="billing-tab-3">
        {tabValue === TAB_PAPER && (
          <Box sx={{ pt: 0 }}>
            <PaperPurchases />
          </Box>
        )}
      </Box>
      <Box role="tabpanel" hidden={tabValue !== TAB_MOBILE} id="billing-panel-4" aria-labelledby="billing-tab-4">
        {tabValue === TAB_MOBILE && (
          <Box sx={{ pt: 0 }}>
            <MobileUsers />
          </Box>
        )}
      </Box>
      {showRefundRequests && (
        <Box role="tabpanel" hidden={tabValue !== TAB_REFUND_REQUESTS} id="billing-panel-5" aria-labelledby="billing-tab-5">
          {tabValue === TAB_REFUND_REQUESTS && (
            <Box sx={{ pt: 0 }}>
              <RefundRequests onPendingCount={setPendingRequests} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AdminBillingTransactions;
